import { apiGet, apiPatch, apiPost } from './client.js';
import type { PaginatedMeta } from './tickets.js';

export type PoStatus = 'draft' | 'sent' | 'partially_received' | 'received' | 'cancelled';

export interface PurchaseOrderListRow {
  id: string;
  poNumber: string;
  supplierId: string;
  supplierName: string | null;
  status: PoStatus;
  total: string;
  expectedAt: string | null;
  createdAt: string;
  lineCount?: number;
  unitsOrdered?: number;
}

export interface PoLineDetail {
  id: string;
  itemId: string;
  itemName: string | null;
  supplierVariantId: string | null;
  qtyOrdered: number;
  qtyReceived: number;
  unitCost: string;
  actualUnitCost: string | null;
  lineTotal: string;
  notes: string | null;
}

export interface PurchaseOrderDetail extends PurchaseOrderListRow {
  lines: PoLineDetail[];
  notes: string | null;
  supplierRef: string | null;
  trackingNumber: string | null;
  shippingCost: string;
  subtotal: string;
  createdBy: string | null;
}

export function listPurchaseOrders(params?: { page?: number; pageSize?: number; status?: PoStatus }) {
  return apiGet<PurchaseOrderListRow[], PaginatedMeta>('/api/v1/purchase-orders', { params });
}

export function getPurchaseOrder(id: string) {
  return apiGet<PurchaseOrderDetail>(`/api/v1/purchase-orders/${id}`).then((r) => r.data);
}

export function createPurchaseOrder(body: {
  supplierId: string;
  expectedAt?: string;
  shippingCost?: number;
  notes?: string;
  lines: Array<{
    itemId: string;
    supplierVariantId?: string;
    qtyOrdered: number;
    unitCost: number;
    notes?: string;
  }>;
}) {
  return apiPost<PurchaseOrderDetail, typeof body>('/api/v1/purchase-orders', body).then((r) => r.data);
}

export function patchPurchaseOrder(
  id: string,
  body: {
    trackingNumber?: string | null;
    supplierRef?: string | null;
    notes?: string | null;
    expectedAt?: string | null;
  },
) {
  return apiPatch<PurchaseOrderDetail, typeof body>(`/api/v1/purchase-orders/${id}`, body).then((r) => r.data);
}

export function sendPurchaseOrder(id: string) {
  return apiPost<PurchaseOrderDetail, Record<string, never>>(`/api/v1/purchase-orders/${id}/send`, {}).then(
    (r) => r.data,
  );
}

export function receivePurchaseOrder(
  id: string,
  body: {
    lines: Array<{
      poLineId: string;
      qtyReceived: number;
      actualUnitCost?: number;
    }>;
  },
) {
  return apiPost<
    PurchaseOrderDetail & { partsFulfilled?: Array<{ ticketId: string; ticketNumber: string; partName: string }> },
    typeof body
  >(`/api/v1/purchase-orders/${id}/receive`, body).then((r) => r.data);
}

export function cancelPurchaseOrder(id: string) {
  return apiPost<PurchaseOrderDetail, Record<string, never>>(`/api/v1/purchase-orders/${id}/cancel`, {}).then(
    (r) => r.data,
  );
}

export function getLowStockForPo() {
  return apiGet<
    Array<{
      supplierId: string | null;
      supplierName: string | null;
      items: Array<{
        itemId: string;
        itemName: string;
        qtyOnHand: string;
        reorderPoint: number;
        reorderQty: number;
      }>;
    }>
  >('/api/v1/purchase-orders/low-stock').then((r) => r.data);
}
